export const UNATTRIBUTED_APP_IDENTIFIER = "__screen0_unattributed__";
export const UNATTRIBUTED_APP_NAME = "Other";

export type ReconciledUsageTotal = {
  totalSeconds: number;
  unattributedSeconds: number;
};

export type ReconcilableApp = {
  appIdentifier?: string;
  displayName: string;
  durationSeconds: number;
  category?: string;
  isEstimated?: boolean;
  color?: string;
  opens?: number;
  hourly?: Array<{ hour: number; seconds: number; opens?: number }>;
};

function isUnattributedApp(app: Pick<ReconcilableApp, "appIdentifier" | "displayName">): boolean {
  return app.appIdentifier === UNATTRIBUTED_APP_IDENTIFIER || app.displayName === UNATTRIBUTED_APP_NAME;
}

/**
 * Mirrors iOS `ScreenTimeTotalReconciliation`: segment-level Screen Time totals
 * (DeviceActivity `ActivitySegment.totalActivityDuration`) are authoritative and
 * can exceed the sum of per-app rows after reinstall/orphaned usage.
 */
export function reconcileUsageTotal(
  segmentTotalSeconds: number,
  knownAppsTotalSeconds: number,
): ReconciledUsageTotal {
  const segmentInt = Math.round(segmentTotalSeconds);
  const knownInt = Math.round(knownAppsTotalSeconds);
  const authoritative = segmentInt > 0 ? segmentInt : knownInt;
  const unattributedSeconds = Math.max(0, authoritative - knownInt);
  return { totalSeconds: authoritative, unattributedSeconds };
}

function scaleHourlyBuckets(
  hourly: Array<{ hour: number; seconds: number; opens?: number }>,
  targetDurationSeconds: number,
  targetOpens?: number,
): Array<{ hour: number; seconds: number; opens?: number }> {
  const hourSum = hourly.reduce((sum, bucket) => sum + bucket.seconds, 0);
  if (hourSum <= 0) return hourly;

  const scale = targetDurationSeconds / hourSum;
  let buckets = hourly.map((bucket) => ({
    hour: bucket.hour,
    seconds: bucket.seconds * scale,
    opens: bucket.opens,
  }));

  const scaledSum = buckets.reduce((sum, bucket) => sum + bucket.seconds, 0);
  const drift = targetDurationSeconds - scaledSum;
  if (drift !== 0) {
    const peakIndex = buckets.reduce(
      (best, bucket, index, list) => (bucket.seconds > list[best]!.seconds ? index : best),
      0,
    );
    buckets = buckets.map((bucket, index) =>
      index === peakIndex ? { ...bucket, seconds: bucket.seconds + drift } : bucket,
    );
  }

  if (targetOpens != null && buckets.some((bucket) => bucket.opens != null)) {
    const opensSum = buckets.reduce((sum, bucket) => sum + (bucket.opens ?? 0), 0);
    if (opensSum > 0 && opensSum !== targetOpens) {
      const opensScale = targetOpens / opensSum;
      buckets = buckets.map((bucket) => ({
        ...bucket,
        opens: bucket.opens != null ? bucket.opens * opensScale : bucket.opens,
      }));
    }
  }

  return buckets;
}

function scaleAppRow<T extends ReconcilableApp>(app: T, scale: number): T {
  const durationSeconds = app.durationSeconds * scale;
  const opens = app.opens != null ? app.opens * scale : undefined;
  const hourly = app.hourly?.length
    ? scaleHourlyBuckets(app.hourly, durationSeconds, opens)
    : app.hourly;
  return {
    ...app,
    durationSeconds,
    opens,
    hourly,
  };
}

function fixDurationRoundingDrift<T extends ReconcilableApp>(apps: T[], targetTotal: number): T[] {
  const rounded = apps.map((app) => ({
    ...app,
    durationSeconds: Math.round(app.durationSeconds),
    opens: app.opens != null ? Math.round(app.opens) : undefined,
  }));
  const sum = rounded.reduce((total, app) => total + app.durationSeconds, 0);
  const drift = targetTotal - sum;
  if (drift === 0 || rounded.length === 0) return rounded;

  const peakIndex = rounded.reduce(
    (best, app, index, list) => (app.durationSeconds > list[best]!.durationSeconds ? index : best),
    0,
  );
  return rounded.map((app, index) =>
    index === peakIndex
      ? { ...app, durationSeconds: Math.max(0, app.durationSeconds + drift) }
      : app,
  );
}

/**
 * Reconcile per-app rows with an authoritative daily total. When the known app
 * sum exceeds the total (same-day merge inflation), scales rows down
 * proportionally. When the sum is lower, appends an "Other" row for the gap.
 */
export function reconcileTopAppsToTotal<T extends ReconcilableApp>(
  apps: T[],
  authoritativeTotalSeconds: number,
): T[] {
  const withoutOther = apps.filter((app) => !isUnattributedApp(app));
  const existingOther = apps.find(isUnattributedApp);
  const knownSum = withoutOther.reduce((sum, app) => sum + app.durationSeconds, 0);
  const authoritative = Math.round(authoritativeTotalSeconds);

  if (authoritative <= 0) {
    return knownSum > 0 ? [...withoutOther].sort((a, b) => b.durationSeconds - a.durationSeconds) : apps;
  }

  if (knownSum > authoritative + 1) {
    const scale = authoritative / knownSum;
    const scaled = withoutOther.map((app) => scaleAppRow(app, scale));
    return fixDurationRoundingDrift(scaled, authoritative)
      .sort((a, b) => b.durationSeconds - a.durationSeconds);
  }

  const { unattributedSeconds } = reconcileUsageTotal(authoritative, knownSum);
  const result = [...withoutOther];
  if (unattributedSeconds > 0) {
    result.push({
      appIdentifier: UNATTRIBUTED_APP_IDENTIFIER,
      displayName: UNATTRIBUTED_APP_NAME,
      durationSeconds: unattributedSeconds,
      color: existingOther?.color,
      category: existingOther?.category,
    } as T);
  }

  return result.sort((a, b) => b.durationSeconds - a.durationSeconds);
}
